// libraries
import React, { useEffect, useState } from "react";
import copy from "clipboard-copy";
import styled from "styled-components";
import { saveAs } from "file-saver";
import { Space, Col, Row, Tooltip } from "antd";
import { RootState } from "store";
import { useSelector } from "react-redux";

// components
import ButtonLeftIcon from "modules/Common/Components/ButtonLeftIcon";
import Message from "modules/Common/Components/Message";
import Modal from "antd/lib/modal/Modal";
import Input from "../../Common/Components/Input";
import Checkbox from "../../Common/Components/Checkbox";
import { generateRssFeedLink, resetRSSFeed } from "../action";
import { RSS_FEED_MODAL_Z_INDEX } from "constants/styles";
import SmallButton from "../../Content/Shared/SmallButton";

// Icons
import Xiq from "../../../assets/img/PublishMenu/Xiq.png";
import XiqBox from "../../../assets/img/PublishMenu/XiqBox.png";
import SalesX from "../../../assets/img/PublishMenu/SalesX.png";
import Linkedin from "../../../assets/img/PublishMenu/Linkedin.png";
import Twitter from "../../../assets/img/PublishMenu/Twitter.png";
import Marketo from "../../../assets/img/PublishMenu/Marketo.png";
import Hubspot from "../../../assets/img/PublishMenu/Hubspot.png";
import Html from "../../../assets/img/PublishMenu/Html.png";
import Eloqua from "../../../assets/img/PublishMenu/Eloqua.png";
import ActiveCampaign from "../../../assets/img/PublishMenu/ActiveCampaign.png";
import MarketingCloud from "../../../assets/img/PublishMenu/MarketingCloud.png";
import HtmlIcon from "../../../assets/img/icons/Html.svg";
import RssIcon from "../../../assets/img/icons/Rss.svg";
import { ReactComponent as RssReactIcon } from "../../../assets/img/icons/Rss.svg";
import CopyIcon from "../../../assets/img/icons/copy_icon_grey.svg";
import { ReactComponent as Cross } from "../../../assets/img/icons/cross.svg";
import Outlook from "../../../assets/img/PublishMenu/Outlook.png";
import Rss from "../../../assets/img/PublishMenu/Rss.png";
import { getQueryCampaignId, getQueryEmailId } from "modules/EmailBrowser/utils";
import {
  sendEmailWithOutlook,
  shareEmailOnSocial,
  getHTMLLink,
  getPromoCodes,
  exportWidgets,
} from "modules/Publish/actions";
import { shareEmailOnLinkedIn, shareEmailOnTwitter } from "modules/Publish/utils";
import { API_BASE_PUBLISH } from "constants/index";
import { IPromoCode } from "modules/Publish/interfaces";
import AccessCodesLoader from "./AccessCodesLoader";

interface IPublishMenuProps {
  setPopoverState?: (value: boolean) => void;
}

const PublishMenu: React.FC<IPublishMenuProps> = (props: IPublishMenuProps) => {
  // showing a message either error or success
  const [message, setMessage] = useState({
    status: false,
    message: "",
    type: "",
    label: "",
  });

  // showing rss link is copied
  const [rssCopied, setRssCopied] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [promoCodes, setPromoCodes] = useState<IPromoCode[]>([]);
  const [searchedPromoCodes, setSearchedPromoCodes] = useState<IPromoCode[]>([]);
  const [showSearchePromoCodes, setShowSearchePromoCodes] = useState<boolean>(false);
  const [searchAccessCodeValue, setSearchAccessCodeValue] = useState<string>("");
  const [ellipsisState, setEllipsistate] = useState(false);
  // getting the rss link after calling api
  const { open, load, link, success, error } = useSelector((state: RootState) => state.design.rss);
  const previewHtml = useSelector((state: RootState) => state.design.data.email_html);
  const emailType = useSelector((state: RootState) => state.editionData?.emailType.toString());
  const promoCodesLoading = useSelector((state: RootState) => state.publish.promoCodesLoading);
  const promoCodesReducer = useSelector((state: RootState) => state.publish.promoCodes);
  const isAuthorized: boolean = useSelector((state: RootState) => state.publish.isAuthorized);
  const exportWidgetPermission: boolean = useSelector(
    (state: RootState) => state.user.userData.export_widget_permission
  );

  const campaignId = getQueryCampaignId().toString();
  const emailId = getQueryEmailId().toString();

  const handleCopyHTMLtoClipboard = () => {
    // for showing copy message
    setMessage({
      status: true,
      message: "",
      type: "success",
      label: "Copied!",
    });
    copy(previewHtml);
    // for hiding copy message
    setTimeout(() => {
      setMessage({
        status: false,
        message: "",
        type: "success",
        label: "Copied!",
      });
    }, 3000);
  };

  const handleCopyHTMLLinkToClipboard = () => {
    let data = new FormData();
    data.append("email_id", getQueryEmailId().toString());
    getHTMLLink(data).then((res) => {
      const data = res.data;
      if (data.success) {
        setMessage({
          status: true,
          message: "",
          type: "success",
          label: "Copied!",
        });
        copy(`${API_BASE_PUBLISH}export_email_html_link/?email_id=${data.uuid_campaign_id}`);
        // for hiding copy message
        setTimeout(() => {
          setMessage({
            status: false,
            message: "",
            type: "success",
            label: "Copied!",
          });
        }, 3000);
      }
    });
  };

  // handle generated HTML
  const handleGenerateHTML = () => {
    let file = new File([previewHtml], "export.htm", {
      type: "text/plain;charset=utf-8",
    });
    saveAs(file);
  };

  // generate Rss feed API calling
  const handleGenerateRSS = () => {
    generateRssFeedLink();
  };

  // handling copy rss link
  const handleCopyRss = () => {
    copy(link);
    setRssCopied(true);
    setTimeout(() => {
      setRssCopied(false);
    }, 3000);
  };

  const emailWithOutlookHandler = () => {
    props.setPopoverState && props.setPopoverState(false);
    sessionStorage.removeItem("no_redirect");
    !isAuthorized && sendEmailWithOutlook(campaignId, emailId, "1", emailType);
  };

  const updatepromoCodesToBeExported = (updatedpromoCodesData: IPromoCode[]) => {
    // this function returns the ids of the promocodes that have been selected
    let checkedPromoCodeIdsHelperArray: string[] = [];
    updatedpromoCodesData.map((promoCode: IPromoCode) => {
      if (promoCode.selected === 1) checkedPromoCodeIdsHelperArray.push(promoCode.id.toString());
    });
    return checkedPromoCodeIdsHelperArray;
  };

  const showModal = () => {
    props?.setPopoverState && props.setPopoverState(false);
    setSearchAccessCodeValue("");
    setIsModalOpen(true);
    getPromoCodes(campaignId);
    setShowSearchePromoCodes(false);
  };

  const searchPromoCodeHandler = (value: string) => {
    if (value.length > 0) {
      const searchedPromoCodes = promoCodes.filter((promoCode: IPromoCode) =>
        promoCode.name.toLowerCase().includes(value.toLowerCase())
      );
      setSearchedPromoCodes(searchedPromoCodes);
      setShowSearchePromoCodes(true);
    } else {
      setShowSearchePromoCodes(false);
    }
  };

  useEffect(() => {
    searchPromoCodeHandler(searchAccessCodeValue);
  }, [searchAccessCodeValue]);

  const checkboxClickHandler = (updatedpromoCode: IPromoCode) => {
    const updatedpromoCodesArray = promoCodes.map((promoCode: IPromoCode) => {
      if (promoCode.id === updatedpromoCode.id) {
        promoCode.selected = promoCode.selected === 1 ? 0 : 1;
      }
      return promoCode;
    });
    setPromoCodes(updatedpromoCodesArray);
  };

  const handleExport = () => {
    setIsModalOpen(false);
    const exportedPromoCodeIds = updatepromoCodesToBeExported(promoCodes);
    exportWidgets(exportedPromoCodeIds);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (promoCodesReducer.length > 0) {
      setPromoCodes(promoCodesReducer);
    }
  }, [promoCodesReducer]);

  return (
    <div className="publishMenu d-flex flex-column">
      <div className="xiq-direct">
        <div className="d-flex justify-content-end cross">
          <Cross
            style={{ cursor: "pointer" }}
            onClick={() => props?.setPopoverState && props.setPopoverState(false)}
          />
        </div>
        <h3>Integrations</h3>
        <ul>
          <li>
            <a onClick={emailWithOutlookHandler}>
              <span>
                <img src={Outlook} alt="Email with Outlook" />
              </span>
              Email with Outlook
            </a>
          </li>
          <li>
            <a onClick={(e) => shareEmailOnSocial({ emailId: getQueryEmailId(), platform: "LinkedIn" })}>
              <span>
                <img src={Linkedin} alt="Send content to Sales App" />
              </span>
              Share on LinkedIn
            </a>
          </li>
          <li>
            <a onClick={(e) => shareEmailOnSocial({ emailId: getQueryEmailId(), platform: "Twitter" })}>
              <span>
                <img src={Twitter} alt="Send content to Sales App" />
              </span>
              Share on Twitter
            </a>
          </li>
          <li hidden={emailType.toString() === "1" || !exportWidgetPermission}>
            <a onClick={showModal}>
              <span>
                <img src={XiqBox} alt="Send content to Sales App" />
              </span>
              Export to Sales Xelerator
            </a>
          </li>
        </ul>
      </div>
      <div className="xiq-export">
        <h3>Export HTML or RSS:</h3>

        <Modal
          maskStyle={{ zIndex: RSS_FEED_MODAL_Z_INDEX }}
          zIndex={RSS_FEED_MODAL_Z_INDEX}
          visible={open}
          destroyOnClose={true}
          onCancel={() => {
            resetRSSFeed();
          }}
          centered
          footer={null}
          closeIcon={<Cross />}
        >
          <ModalInnerWrapper>
            <h1>
              <RssReactIcon /> EXPORT TO RSS
            </h1>
            {error && (
              <Message type={"error"} label={"Error!"} message={"Failed to Load RSS feed"} marginBottom={15} />
            )}
            {rssCopied && (
              <Message
                type={"success"}
                label={"Copied!"}
                message={"RSS feed link is copied to the clipboard!"}
                marginBottom={15}
              />
            )}
            {load && `Loading...`}
            <a className="link" href={link} target="__blank">
              {link}
            </a>
            <ButtonLeftIcon className={"button-top"} label="Copy" icon={CopyIcon} onClick={handleCopyRss} />
          </ModalInnerWrapper>
        </Modal>

        <Modal
          visible={isModalOpen}
          onCancel={handleCancel}
          closeIcon={<Cross />}
          destroyOnClose={true}
          footer={null}
          wrapClassName="widget-wrapper"
          maskClosable={false}
        >
          <PromoCodeModalWrapper>
            <div className="widget-body">
              <h1>Export Widgets</h1>
              <div className="search-section">
                <h4>Search Access Code</h4>
                <Input
                  value={searchAccessCodeValue}
                  onChange={(e: any) => setSearchAccessCodeValue(e.target.value.trim())}
                  labelProps={{ margin: "0" }}
                  type="text"
                  placeholder="Search Access Code"
                />
              </div>
              <div className="scrollable">
                {promoCodesLoading ? (
                  <AccessCodesLoader />
                ) : (
                  <>
                    {showSearchePromoCodes && searchedPromoCodes.length === 0 ? (
                      <p className="message">No access code(s) found against your search.</p>
                    ) : promoCodes.length === 0 ? (
                      <p className="message">No access code(s) available.</p>
                    ) : null}
                    {showSearchePromoCodes
                      ? searchedPromoCodes.map((promoCode: IPromoCode) => {
                          return (
                            <label key={promoCode.id}>
                              <Checkbox
                                checked={promoCode.selected === 1}
                                onChange={() => checkboxClickHandler(promoCode)}
                              />
                              <Tooltip placement="top" title={promoCode.name}>
                                <span>{promoCode.name}</span>
                              </Tooltip>
                            </label>
                          );
                        })
                      : promoCodes.map((promoCode: any) => {
                          return (
                            <label key={promoCode.id}>
                              <Checkbox
                                checked={promoCode.selected === 1}
                                onChange={() => checkboxClickHandler(promoCode)}
                              />
                              <Tooltip placement="top" title={promoCode.name}>
                                <span>{promoCode.name}</span>
                              </Tooltip>
                            </label>
                          );
                        })}
                  </>
                )}
              </div>
              <div className="bottom-floating-action">
                <SmallButton onClick={handleCancel} title="Cancel" outline="outline" />
                <SmallButton onClick={handleExport} title="Apply" />
              </div>
            </div>
          </PromoCodeModalWrapper>
        </Modal>

        <ButtonLeftIcon label="Copy HTML to Clipboard" onClick={handleCopyHTMLtoClipboard} icon={HtmlIcon} />
        <ButtonLeftIcon
          label="Copy HTML Link"
          onClick={handleCopyHTMLLinkToClipboard}
          icon={HtmlIcon}
          style={{ marginTop: "10px" }}
        />
        <Row gutter={24} className="py-2">
          <Col span={12}>
            <ButtonLeftIcon label="Generate HTML file" onClick={handleGenerateHTML} icon={HtmlIcon} />
          </Col>
          <Col span={12}>
            <ButtonLeftIcon onClick={handleGenerateRSS} label="Generate RSS Feed" icon={RssIcon} />
          </Col>
        </Row>
        {message.status && (
          <Message type={message.type} label={message.label} message={message.message} marginBottom={15} />
        )}
        <div className="separator mt-1">
          <span>works with</span>
        </div>
        <div className="workWith">
          <div className="item">
            <span>
              <img src={Outlook} alt="Send content to Sales App" />
            </span>
            Outlook,
          </div>
          <div className="item">
            <span>
              <img src={Html} alt="Send content to Sales App" />
            </span>
            HTML,
          </div>
          <div className="item">
            <span>
              <img src={Rss} alt="Send content to Sales App" />
            </span>
            RSS,
          </div>
          <div className="item">
            <span>
              <img src={MarketingCloud} alt="Send content to Sales App" />
            </span>
            Marketing Cloud,
          </div>
          <div className="item">
            <span>
              <img src={Marketo} alt="Send content to Sales App" />
            </span>
            Marketo,
          </div>
          <div className="item">
            <span>
              <img src={Eloqua} alt="Send content to Sales App" />
            </span>
            Eloqua,
          </div>
          <div className="item">
            <span>
              <img src={Hubspot} alt="Send content to Sales App" />
            </span>
            Hubspot,
          </div>
          <div className="item">
            <span>
              <img src={ActiveCampaign} alt="Send content to Sales App" />
            </span>
            ActiveCampaign
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishMenu;

const PromoCodeModalWrapper = styled.div`
  .message {
    color: #5e7079;
    font-size: 20px;
    margin-bottom: 10px;
    font-family: "Roboto Slab";
    font-weight: 500;
    letter-spacing: -0.56px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
    }
  }
`;

const ModalInnerWrapper = styled.div`
  padding: 30px 50px;
  h1 {
    color: #434d69;
    font-size: 24px;
    line-height: 38px;
    font-family: "Roboto Slab", serif;
  }
  .button-top {
    margin-top: 30px;
  }
  .link {
    color: #37afe9;
    margin-bottom: 15px;
  }
  .link:hover {
    color: #ffc107;
    text-decoration: none;
  }
`;
// const Modal = styled.div`
//   .widget-wrapper {
//     padding: 30px 50px;
//     h1 {
//       color: #434d69;
//       font-size: 24px;
//       line-height: 38px;
//       font-family: "Roboto Slab", serif;
//     }
//   }
// `;
